import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';

/**
 * PageTour - Interactive step-by-step guided tour for any page.
 * Uses CSS box-shadow spotlight technique for a clear, reliable highlight effect.
 * Portal-rendered to avoid overflow clipping from parent containers.
 */
export default function PageTour({ steps = [], storageKey, autoStart = false }) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const findTarget = useCallback(() => {
    if (!active || !steps[step]) return;
    const rawSelectors = steps[step].target.split(',').map(s => s.trim()).filter(Boolean);
    // Build an extended list: for every [data-testid="foo"] selector, also try "foo-2" / "foo-1"
    // because earlier data-testid dedup pass renamed many primary buttons to a -2 suffix.
    const selectors = [];
    for (const sel of rawSelectors) {
      selectors.push(sel);
      const m = sel.match(/^\[data-testid="([^"]+)"\]$/);
      if (m) {
        selectors.push(`[data-testid="${m[1]}-2"]`);
        selectors.push(`[data-testid="${m[1]}-1"]`);
      }
    }
    for (const sel of selectors) {
      try {
        const el = document.querySelector(sel);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const r = el.getBoundingClientRect();
              setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            });
          });
          return;
        }
      } catch { /* malformed selector — try next */ }
    }
    // No target found — render tooltip inline (not centered on dark screen) so the
    // user still sees the guidance.
    setRect(null);
  }, [active, step, steps]);

  useEffect(() => {
    findTarget();
    const update = () => findTarget();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [findTarget]);

  useEffect(() => {
    if (autoStart && storageKey && !localStorage.getItem(storageKey)) {
      const timer = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [autoStart, storageKey]);

  useEffect(() => {
    if (active) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [active]);

  const close = () => {
    setActive(false);
    setStep(0);
    if (storageKey) localStorage.setItem(storageKey, 'seen');
  };

  const next = () => { if (step < steps.length - 1) setStep(s => s + 1); else close(); };
  const prev = () => { if (step > 0) setStep(s => s - 1); };

  if (!steps.length) return null;

  // Tooltip positioning
  const TW = 340;
  const GAP = 18;
  const PAD = 12; // spotlight padding around target
  let ttTop = 0, ttLeft = 0;

  if (rect) {
    const spotBottom = rect.top + rect.height + PAD;
    const spotTop = rect.top - PAD;
    const TH_EST = 210;

    const spaceBelow = window.innerHeight - spotBottom - GAP;
    const spaceAbove = spotTop - GAP;

    if (spaceBelow >= TH_EST) {
      ttTop = spotBottom + GAP;
    } else if (spaceAbove >= TH_EST) {
      ttTop = spotTop - GAP - TH_EST;
    } else {
      ttTop = Math.max(20, (window.innerHeight - TH_EST) / 2);
    }

    const idealLeft = rect.left + rect.width / 2 - TW / 2;
    ttLeft = Math.max(16, Math.min(idealLeft, window.innerWidth - TW - 16));
  } else {
    ttTop = window.innerHeight / 2 - 105;
    ttLeft = window.innerWidth / 2 - TW / 2;
  }

  const overlay = active ? (
    <div data-testid="tour-overlay">
      {/* Click-to-close backdrop — only rendered when spotlight is NOT active */}
      {!rect && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 99998,
            background: 'rgba(0,0,0,0.75)',
          }}
        />
      )}

      {/* Spotlight window — a div exactly over the target with a giant box-shadow */}
      {rect && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 12,
            zIndex: 99998,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.75)',
            pointerEvents: 'none',
          }}
          data-testid="tour-spotlight"
        />
      )}

      {/* Animated spotlight ring */}
      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD - 2,
            left: rect.left - PAD - 2,
            width: rect.width + PAD * 2 + 4,
            height: rect.height + PAD * 2 + 4,
            borderRadius: 14,
            zIndex: 99999,
            border: '2.5px solid #f97316',
            boxShadow: '0 0 0 4px rgba(249,115,22,0.25), 0 0 20px rgba(249,115,22,0.3), inset 0 0 12px rgba(249,115,22,0.08)',
            pointerEvents: 'none',
            animation: 'tour-spotlight-pulse 2s ease-in-out infinite',
          }}
        />
      )}

      {/* Click layer over darkened area to close tour */}
      {rect && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 99997,
            pointerEvents: 'auto',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: ttTop,
          left: ttLeft,
          width: TW,
          zIndex: 100000,
          pointerEvents: 'auto',
        }}
        className="bg-white dark:bg-muted rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.35)] border border-border dark:border-stone-700"
        data-testid="tour-tooltip"
      >
        <div className="p-5">
          {/* Close */}
          <button
            onClick={close}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-muted dark:bg-stone-700 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted dark:hover:bg-stone-600 transition-colors"
            data-testid="tour-close-btn"
          >
            <X size={13} />
          </button>

          {/* Step badge */}
          <div className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 px-2.5 py-1 rounded-full mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
            <span className="text-[11px] font-bold uppercase tracking-[0.1em]">Step {step + 1} of {steps.length}</span>
          </div>

          {/* Title */}
          <h3 className="text-[16px] font-bold text-foreground dark:text-white mb-2 pr-6 leading-tight">
            {steps[step]?.title}
          </h3>

          {/* Content */}
          <p className="text-[13px] text-muted-foreground dark:text-muted-foreground leading-relaxed mb-5">
            {steps[step]?.content}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-300 ${
                    i === step ? 'w-5 h-2 bg-orange-500' : 'w-2 h-2 bg-muted dark:bg-stone-600'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button size="sm" variant="ghost" onClick={prev} className="h-8 text-xs px-3 text-muted-foreground hover:text-foreground">
                  <ChevronLeft size={14} /> Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={next}
                className="h-8 text-xs rounded-full px-5 bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20"
                data-testid="tour-next-btn"
              >
                {step === steps.length - 1 ? 'Done' : <>Next <ChevronRight size={14} /></>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyframe animation for spotlight pulse */}
      <style>{`
        @keyframes tour-spotlight-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(249,115,22,0.25), 0 0 20px rgba(249,115,22,0.3), inset 0 0 12px rgba(249,115,22,0.08); }
          50% { box-shadow: 0 0 0 6px rgba(249,115,22,0.35), 0 0 30px rgba(249,115,22,0.45), inset 0 0 16px rgba(249,115,22,0.12); }
        }
      `}</style>
    </div>
  ) : null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => { setStep(0); setActive(true); }}
        className="gap-1.5 text-xs rounded-full border-dashed"
        data-testid="take-tour-btn"
      >
        <HelpCircle size={14} />
        Take a Tour
      </Button>

      {overlay && createPortal(overlay, document.body)}
    </>
  );
}
